import { ActionParamValue } from '10.quickConnect.app/components/domain/Home/Declarations/DeclarationHistory/types';
import { getAllParentsFieldsAreVisible } from '50.quickConnect.Fields/helpers/visibility';
import { errorHandler } from '80.quickConnect.Core/helpers';
import { FieldType } from '90.quickConnect.Models/enums';
import { mapToFieldData } from '90.quickConnect.Models/mappings';
import { FieldData, FieldDesc } from '90.quickConnect.Models/models';

const tag = '50.quickConnect.Fields/FieldsTypes/Others/QcAction/utils/index.ts';

const checkActionCriterias = (field: FieldDesc, paramsIds: string[]): boolean => {
  const { fieldType, id } = field;

  if (fieldType === FieldType.Action) return false;

  const normalizedId = id.toLocaleLowerCase();

  return paramsIds.some((paramId: string) => normalizedId.startsWith(paramId));
};

const checkFieldsForAddActionParams = (
  declaration: FieldDesc[],
  { items: actionParams = [], fullPathId: actionFullPathId }: FieldDesc,
): FieldDesc[] => {
  const declarationParamsIds = actionParams.reduce((acc: string[], { id }: FieldDesc) => {
    if (id.endsWith('*')) {
      acc.push(id.slice(0, -1).toLocaleLowerCase());
    }

    return acc;
  }, []);

  if (declarationParamsIds.length === 0) return [];

  const otherFields = declaration.filter(({ fullPathId }: FieldDesc) => fullPathId !== actionFullPathId);

  return otherFields.filter((field: FieldDesc) => checkActionCriterias(field, declarationParamsIds));
};

const excludeMediaAndContainerFields = (fieldDesc: FieldDesc): boolean => {
  const { fieldType } = fieldDesc;
  // Ne pas muter fieldDesc.items ici car cela modifie la déclaration originale !
  // Les items sont déjà filtrés au niveau parent par flatten() et les filtres suivants

  return (
    fieldType !== FieldType.Attachment &&
    fieldType !== FieldType.Audio &&
    fieldType !== FieldType.Draw &&
    fieldType !== FieldType.FixedAttachment &&
    fieldType !== FieldType.FixedImage &&
    fieldType !== FieldType.Photo &&
    fieldType !== FieldType.Signature &&
    fieldType !== FieldType.Dialog &&
    fieldType !== FieldType.Group &&
    fieldType !== FieldType.Include &&
    fieldType !== FieldType.RepeatableGroup
  );
};

// Excluded field types for action params: media fields, container fields and list fields
const excludeNonActionParamFields = (fieldDesc: FieldDesc): boolean => {
  const { fieldType } = fieldDesc;

  switch (fieldType) {
    case FieldType.Attachment:
    case FieldType.Audio:
    case FieldType.Draw:
    case FieldType.FixedAttachment:
    case FieldType.FixedImage:
    case FieldType.Photo:
    case FieldType.Signature:
    case FieldType.Dialog:
    case FieldType.Group:
    case FieldType.Include:
    case FieldType.RepeatableGroup:
    case FieldType.Combo:
    case FieldType.RadioList:
    case FieldType.CheckBoxList:
    case FieldType.HierarchicalList:
      return false;

    default:
      return true;
  }
};

const excludeNoVisibleField = (fieldDesc: FieldDesc, _: number, fields: FieldDesc[]): boolean => {
  const { isVisible, isVisibleOnDevice, fullPathId } = fieldDesc;

  const bVisibility = isVisibleOnDevice ?? isVisible;

  if (!fullPathId.includes('.')) return bVisibility;

  // Il faut vérifier si les parents sont visibles
  const bVisibilityParents = getAllParentsFieldsAreVisible(fields, fullPathId);

  return bVisibility && bVisibilityParents;
};

const excludeFieldWithNoValues = (fieldDesc: FieldDesc): boolean => {
  const { value } = fieldDesc;
  // Ne pas muter fieldDesc.items ici car cela modifie la déclaration originale !
  // Les items sont déjà filtrés au niveau parent par flatten() et les filtres suivants

  return value !== undefined;
};

const excludeDeclarationReferencedParams = ({ id }: FieldDesc): boolean => !id.endsWith('*');

const mapToActionParamValues = ({ label, value, id, fieldType }: FieldData): ActionParamValue => ({
  id,
  fieldType,
  label: label ?? undefined,
  value,
});

export const getActionParamFromDeclaration = (declaration: FieldDesc[], actionFullId: string): ActionParamValue[] => {
  try {
    const actionField = declaration.find((field: FieldDesc) => field.fullPathId === actionFullId);

    if (!actionField) {
      throw new Error(`Aucun champ action avec le fullPathId: ${actionFullId} n'a été trouvé`);
    }

    const declarationParams = checkFieldsForAddActionParams(declaration, actionField)
      .filter(excludeNoVisibleField)
      .filter(excludeNonActionParamFields)
      .filter(excludeFieldWithNoValues)
      .map((field) => mapToFieldData(field, true))
      .map(mapToActionParamValues);

    const itemsParams = (actionField.items ?? [])
      .filter(excludeDeclarationReferencedParams)
      .filter(excludeFieldWithNoValues)
      .filter(excludeNonActionParamFields)
      .map((field) => mapToFieldData(field, false))
      .map(mapToActionParamValues);

    return [...declarationParams, ...itemsParams];
  } catch (error) {
    errorHandler(tag, error, 'getActionParamFromDeclaration', 'trace');
    return [];
  }
};
